/* ═══════════════════════════════════════════════════════════════════════════════
   Payment Enhancements Tour (v2.5.0)
   
   Feature update tour for payment system improvements:
   
   1. NEW "Held" action: builder hasn't paid, payment expected in future
      (zeroes out paid amount like voided, but semantically different)
   2. Default state is now "Pending" (not "Paid") after clear
   3. "Clear Payment" button: wipe all payment data for a claim
      (progress + variations + retention released, keeps the claim record)
   4. 2-decimal-place precision on all dollar amounts
   5. Variation "is_paid" toggle: mark individual variation groups as paid
   6. Removed Dashpivot sync disclaimer from payment panel
   
   SEQ-83 fixes: retention held persistence, save overwrite, GST on PDF
   
   Pages: 'project_detail' (payment panels open from Claim Reports tab)
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const paymentEnhancementsTour: TourDefinition = {
  id: 'payment_enhancements_v2_5',
  version: '2.5.0',
  type: 'feature_update',
  audience: 'all',
  page: 'project_detail',
  priority: 4,
  title: 'Payment Updates',
  subtitle: 'New payment actions and variation paid tracking.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'Payment System Updates',
      description: 'We\'ve improved payment tracking with new actions, a clear payment option, and per-variation paid marking.',
      icon: '💰',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Held & Pending Actions ───────────────────────────────
    {
      id: 'held-pending-actions',
      type: 'info_card',
      title: 'New: Held Action',
      description: 'Payment line items now support a "Held" action. Use this when the builder hasn\'t paid yet but payment is expected in future.\n\nHeld items are zeroed out (like voided) but tracked separately so you know what\'s outstanding.',
      icon: '🏦',
      actionLabel: 'Next',
    },

    // ── Step 2: Clear Payment ────────────────────────────────────────
    {
      id: 'clear-payment',
      type: 'info_card',
      title: 'Clear Payment',
      description: 'Need to start over? The payment popover now has a "Clear Payment" button that removes ALL payment data for a claim (progress, variations, and retention) while keeping the claim record itself.',
      icon: '🧹',
      actionLabel: 'Next',
    },

    // ── Step 3: Variation Paid Toggle ────────────────────────────────
    {
      id: 'variation-paid-toggle',
      type: 'info_card',
      title: 'Mark Variations as Paid',
      description: 'The Variations table now shows a paid toggle per variation group. Click the circle icon to mark a docket as "Paid by Builder", and a green check appears when paid.\n\nAll amounts now display with 2-decimal precision for accuracy.',
      icon: '✅',
      actionLabel: 'Got It',
    },

    // ── Step 4: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'All Set!',
      description: 'Use Held for expected payments, Clear Payment to reset, and the paid toggle for quick variation tracking.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
